import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Zap, Code2, Rocket, History, LogOut, User, Lightbulb, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { SignInButton, SignUpButton, UserButton, useAuth, useUser } from "@clerk/clerk-react";
import { useAppAuth } from "@/App";
import PricingModal from "@/components/PricingModal";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;



const FeatureCard = ({ icon: Icon, title, description, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay }}
    className="glass-card rounded-2xl p-6 hover:border-gold-500/30 transition-all duration-300"
  >
    <div className="w-12 h-12 rounded-xl bg-gold-gradient flex items-center justify-center mb-4">
      <Icon className="w-6 h-6 text-black" />
    </div>
    <h3 className="font-primary font-semibold text-lg text-white mb-2">{title}</h3>
    <p className="font-body text-sm text-zinc-400">{description}</p>
  </motion.div>
);

// X (Twitter) Icon
const XIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

// LinkedIn Icon
const LinkedInIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

export default function HomePage() {
  const [idea, setIdea] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { isSignedIn, getToken } = useAuth();
  const { user } = useUser();
  const { canUsePrompt, remainingPrompts, incrementPrompts } = useAppAuth();
  const [brainstormLoading, setBrainstormLoading] = useState(false);
  const [brainstormIdeas, setBrainstormIdeas] = useState([]);
  const [pricingModalOpen, setPricingModalOpen] = useState(false);

  const handleBrainstorm = async () => {
    if (!isSignedIn) {
      toast.error("Please sign in to brainstorm ideas");
      return;
    }

    setBrainstormLoading(true);
    try {
      const token = await getToken();
      const response = await fetch(`${API}/brainstorm`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 429) {
          toast.error("Daily brainstorm limit reached. Try again tomorrow.");
          return;
        }
        throw new Error('Brainstorm failed');
      }

      const data = await response.json();
      setBrainstormIdeas(data.ideas || []);
      toast.success(`Generated ${data.ideas?.length || 0} new ideas! (${data.remaining_today} remaining today)`);
    } catch (error) {
      console.error("Brainstorm error:", error);
      toast.error("Failed to generate ideas. Please try again.");
    } finally {
      setBrainstormLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!idea.trim()) {
      toast.error("Please enter your app idea");
      return;
    }

    if (!isSignedIn) {
      toast.error("Please sign in to analyze your idea");
      return;
    }

    if (!canUsePrompt()) {
      toast.error("You have reached your monthly limit. Please try again next month.");
      return;
    }

    setLoading(true);
    toast.info("Analyzing your idea... This may take 30 to 60 seconds.", { duration: 5000 });

    try {
      const token = await getToken();
      const response = await fetch(`${API}/analyze`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ idea: idea.trim() }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          toast.error("Please sign in to analyze your idea.");
          return;
        }
        if (response.status === 429) {
          toast.error("You have reached your monthly limit.");
          return;
        }
        throw new Error('Analysis failed');
      }

      const data = await response.json();
      sessionStorage.setItem("analysisResult", JSON.stringify(data));
      incrementPrompts();
      toast.success("Analysis complete!");
      navigate("/results");
    } catch (error) {
      console.error("Analysis error:", error);
      toast.error("Failed to analyze idea. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian-300 relative">
      {/* Top Bar */}
      <div className="fixed top-0 left-0 right-0 z-50 px-6 py-4 bg-obsidian-300/80 backdrop-blur-sm border-b border-white/5">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between">
          {/* Logo */}
          <span className="font-secondary font-bold text-sm tracking-[0.3em] uppercase text-gold-400">
            StackVibecode
          </span>

          {/* Auth Buttons */}
          <div className="flex items-center gap-4">
            {isSignedIn ? (
              <>
                {/* Monthly Limit Badge */}
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full glass-card border border-gold-500/20">
                  <span className="text-xs text-zinc-400">Monthly:</span>
                  <span className="text-sm font-bold text-gold-400">{remainingPrompts()}/{2}</span>
                </div>

                {/* Subscribe Button */}
                <button
                  onClick={() => setPricingModalOpen(true)}
                  className="flex items-center gap-2 px-6 py-2 rounded-full gold-gradient text-black font-semibold hover:shadow-gold transition-all"
                  data-testid="subscribe-btn"
                >
                  <CreditCard className="w-4 h-4" />
                  <span className="hidden sm:block">Subscribe</span>
                </button>

                {/* User Button from Clerk */}
                <UserButton
                  afterSignOutUrl="/"
                  appearance={{
                    elements: {
                      avatarBox: "w-8 h-8 border border-gold-500/30"
                    }
                  }}
                />

                <Link
                  to="/history"
                  className="flex items-center gap-2 px-4 py-2 rounded-full glass-card hover:border-gold-500/50 transition-all text-zinc-400 hover:text-gold-400"
                  data-testid="history-link"
                >
                  <History className="w-4 h-4" />
                  <span className="hidden sm:block text-sm">History</span>
                </Link>
              </>
            ) : (
              <>
                {/* Subscribe Button */}
                <button
                  onClick={() => setPricingModalOpen(true)}
                  className="flex items-center gap-2 px-6 py-2 rounded-full gold-gradient text-black font-semibold hover:shadow-gold transition-all"
                  data-testid="subscribe-btn"
                >
                  <CreditCard className="w-4 h-4" />
                  <span className="hidden sm:block">Subscribe</span>
                </button>

                <SignInButton mode="modal">
                  <button
                    className="flex items-center gap-2 px-6 py-2 rounded-full gold-gradient text-black font-semibold hover:shadow-gold transition-all"
                    data-testid="sign-in-btn"
                  >
                    <User className="w-4 h-4" />
                    Sign In
                  </button>
                </SignInButton>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="flex justify-center">
        {/* Main Content */}
        <main className="flex-1 max-w-[1200px] mx-auto px-6 py-12 pt-24">
          {/* Hero Section */}
          <section className="hero-bg min-h-[70vh] flex flex-col items-center justify-center text-center relative rounded-3xl overflow-hidden mb-12">
            <div className="relative z-10 px-6">
              {/* Logo/Brand */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="mb-8"
              >
                <span className="font-secondary font-bold text-sm tracking-[0.3em] uppercase text-gold-400">
                  StackVibecode
                </span>
              </motion.div>

              {/* Headline */}
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="font-secondary font-bold text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight mb-6 max-w-4xl mx-auto text-center"
              >
                <span className="text-white">Turn Ideas Into </span>
                <span className="gold-text">Production Ready</span>
                <span className="text-white"> Tech Stacks</span>
              </motion.h1>

              {/* Subheadline */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="font-body text-base md:text-lg text-zinc-400 max-w-2xl mx-auto mb-12"
              >
                Describe what you want to build. StackVibecode designs your entire tech stack,
                market strategy, and an AI ready build prompt from zero to production.
              </motion.p>

              {/* Input Form */}
              <motion.form
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                onSubmit={handleSubmit}
                className="w-full max-w-3xl mx-auto"
              >
                <div className="relative">
                  <textarea
                    value={idea}
                    onChange={(e) => {
                      setIdea(e.target.value);
                      // Auto-expand textarea height
                      e.target.style.height = 'auto';
                      e.target.style.height = Math.min(e.target.scrollHeight, 300) + 'px';
                    }}
                    onKeyDown={(e) => {
                      // Submit on Enter without Shift
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSubmit(e);
                      }
                    }}
                    placeholder="What are you thinking of building?"
                    className="w-full bg-white/5 border border-white/10 rounded-2xl px-8 py-6 text-lg md:text-xl text-white placeholder:text-white/30 focus:outline-none focus:border-gold-500/50 focus:ring-1 focus:ring-gold-500/50 backdrop-blur-md transition-all shadow-2xl pr-32 resize-none overflow-hidden min-h-[72px]"
                    disabled={loading}
                    data-testid="idea-input"
                    rows={1}
                  />
                  <button
                    type="submit"
                    disabled={loading || !isSignedIn}
                    className="absolute right-3 top-1/2 -translate-y-1/2 px-8 py-3 rounded-xl font-secondary font-bold tracking-widest uppercase text-black gold-gradient gold-glow hover:shadow-gold-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed btn-shimmer"
                    data-testid="submit-button"
                  >
                    {loading ? (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                        <span>Analyzing...</span>
                      </div>
                    ) : (
                      "LFG!"
                    )}
                  </button>
                </div>

                {!isSignedIn && (
                  <p className="mt-4 text-sm text-gold-400">
                    Please sign in to analyze your idea
                  </p>
                )}
              </motion.form>

              {/* Example prompts */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="mt-8 flex flex-wrap justify-center gap-3"
              >
                <span className="text-zinc-500 text-sm">Try:</span>
                {(brainstormIdeas.length > 0 ? brainstormIdeas : [
                  "AI writing assistant",
                  "E commerce marketplace",
                  "Social fitness app",
                ]).map((example, i) => (
                  <button
                    key={i}
                    onClick={() => setIdea(example)}
                    className="px-4 py-2 rounded-full border border-white/10 text-sm text-zinc-400 hover:border-gold-500/50 hover:text-gold-400 transition-all"
                    data-testid={`example-${i}`}
                  >
                    {example}
                  </button>
                ))}
                <button
                  onClick={handleBrainstorm}
                  disabled={brainstormLoading || !isSignedIn}
                  className="px-4 py-2 rounded-full font-secondary font-bold text-sm tracking-wider uppercase text-black gold-gradient hover:shadow-gold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  data-testid="brainstorm-btn"
                >
                  {brainstormLoading ? (
                    <>
                      <div className="w-3 h-3 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                      <span>Thinking...</span>
                    </>
                  ) : (
                    <>
                      <Lightbulb className="w-4 h-4" />
                      <span>Brainstorm</span>
                    </>
                  )}
                </button>
              </motion.div>
            </div>
          </section>

          {/* Features Section */}
          <section className="py-12">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="font-primary font-semibold text-2xl md:text-3xl text-white mb-4">
                What You Get
              </h2>
              <p className="text-zinc-400 max-w-xl mx-auto">
                A complete roadmap from idea to production
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <FeatureCard
                icon={Code2}
                title="Complete Tech Stack"
                description="Frontend, backend, database, hosting, AI models, payment integration and more tailored to your specific idea."
                delay={0.7}
              />
              <FeatureCard
                icon={Zap}
                title="Smart Comparisons"
                description="See alternatives with pros and cons for each technology choice."
                delay={0.8}
              />
              <FeatureCard
                icon={Sparkles}
                title="Market Analysis"
                description="Understand your competitors and find market gaps to exploit."
                delay={0.9}
              />
              <FeatureCard
                icon={Rocket}
                title="AI Build Prompt"
                description="Get a ready to use prompt for AI coding tools to build your app."
                delay={1.0}
              />
            </div>
          </section>

          {/* Footer */}
          <footer className="py-12 pb-32 border-t border-white/5 mt-12">
            <div className="text-center">
              <p className="text-zinc-400 mb-4">A product of Ansari Studio</p>
              <div className="flex items-center justify-center gap-6 mb-6">
                <a
                  href="https://x.com/AnsariStudioxyz"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-zinc-400 hover:text-gold-400 transition-colors"
                  data-testid="twitter-link"
                >
                  <XIcon />
                </a>
                <a
                  href="https://www.linkedin.com/company/ansari-studio"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-zinc-400 hover:text-gold-400 transition-colors"
                  data-testid="linkedin-link"
                >
                  <LinkedInIcon />
                </a>
              </div>
              <div className="flex items-center justify-center gap-4">
                <Link
                  to="/terms"
                  className="text-sm text-zinc-500 hover:text-gold-400 transition-colors"
                >
                  Terms & Conditions
                </Link>
                <span className="text-zinc-600">•</span>
                <Link
                  to="/privacy"
                  className="text-sm text-zinc-500 hover:text-gold-400 transition-colors"
                >
                  Privacy Policy
                </Link>
              </div>
            </div>
          </footer>
        </main>
      </div>

      {/* StackVibecode Footer Banner */}
      <div className="w-full bg-gold-500 py-24 md:py-32 lg:py-40">
        <p className="text-center font-secondary font-bold text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl tracking-[0.15em] uppercase text-black">
          StackVibecode
        </p>
      </div>

      {/* Pricing Modal */}
      <PricingModal
        isOpen={pricingModalOpen}
        onClose={() => setPricingModalOpen(false)}
      />
    </div>
  );
}
